import { init, track } from "@plausible-analytics/tracker/plausible.js";
import { forbiddenUrlStateKeys } from "./lib/state";

const plausibleDomain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
const forbiddenPropKeys = new Set([...forbiddenUrlStateKeys, "query", "search", "url", "href", "state", "patient", "note", "notes"]);

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

let initialized = false;

export function initAnalytics() {
  if (!plausibleDomain || typeof window === "undefined" || initialized) return;

  init({
    domain: plausibleDomain,
    autoCapturePageviews: false,
    outboundLinks: false,
    fileDownloads: false,
    formSubmissions: false,
  });
  initialized = true;
}

export function trackPageView(pathname: string) {
  trackEvent("pageview", { path: sanitizePath(pathname) }, false);
}

export function trackEvent(eventName: string, props: AnalyticsProps = {}, interactive = true) {
  if (!plausibleDomain || typeof window === "undefined") return;

  try {
    track(eventName, {
      interactive,
      props: sanitizeProps(props),
      url: `${window.location.origin}${sanitizePath(window.location.pathname)}`,
    });
  } catch {
    // Analytics must never interrupt clinical workflow.
  }
}

export function sanitizeProps(props: AnalyticsProps): Record<string, string> {
  return Object.fromEntries(
    Object.entries(props)
      .filter(([key, value]) => value !== undefined && !forbiddenPropKeys.has(key))
      .map(([key, value]) => [key, value === null ? "null" : String(value)]),
  );
}

function sanitizePath(pathname: string) {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return normalized.split("?")[0].split("#")[0];
}
